import {
    Component,
    ViewChild,
    OnInit,
    AfterViewInit,
    OnDestroy,
    ChangeDetectorRef,
} from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import {
    Router,
    RouterOutlet,
    NavigationEnd,
    ActivatedRoute,
} from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import {
    ReportCategoryService,
    ReportCategoryMapping,
} from '../../services/report-category.service';
import { LoggerService } from '../../core/services/logger.service';
import { catchError, finalize, filter } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { DeleteReportCategoryDialogComponent } from './delete-report-category-dialog/delete-report-category-dialog.component';

export interface ReportCategoryRow {
    id: string;
    reportId: string;
    reportName: string;
    category: string;
}

@Component({
    selector: 'app-report-categories-page',
    standalone: true,
    imports: [
        CommonModule,
        NgIf,
        RouterOutlet,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatPaginatorModule,
        MatTableModule,
        MatSortModule,
        MatSnackBarModule,
        MatDialogModule,
        MatTooltipModule,
        MatChipsModule,
    ],
    templateUrl: './report-categories-page.component.html',
    styleUrl: './report-categories-page.component.scss',
})
export class ReportCategoriesPageComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    displayedColumns: string[] = [
        'reportName',
        'reportId',
        'category',
        'action',
    ];
    dataSource = new MatTableDataSource<ReportCategoryRow>([]);
    isLoading = false;
    private routerSubscription?: Subscription;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private reportCategoryService: ReportCategoryService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private dialog: MatDialog,
        // eslint-disable-next-line no-unused-vars
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loadMappings();
        this.subscribeToRouterEvents();
    }

    ngOnDestroy(): void {
        this.routerSubscription?.unsubscribe();
    }

    private subscribeToRouterEvents(): void {
        this.routerSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const url = event.urlAfterRedirects;
                if (
                    url === '/modules/report-categories' ||
                    url.startsWith('/modules/report-categories?')
                ) {
                    this.loadMappings();
                }
            });
    }

    ngAfterViewInit(): void {
        if (this.paginator) this.dataSource.paginator = this.paginator;
        if (this.sort) this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (
            item: ReportCategoryRow,
            prop: string
        ) => {
            if (prop === 'reportName') return item.reportName;
            if (prop === 'reportId') return item.reportId;
            if (prop === 'category') return item.category;
            return (item as unknown as Record<string, string>)[prop] ?? '';
        };
    }

    loadMappings(): void {
        this.isLoading = true;
        this.reportCategoryService
            .getMappings()
            .pipe(
                catchError((err) => {
                    this.logger.error(
                        'Error al cargar categorías de reportes',
                        err as Error,
                        'ReportCategoriesPageComponent'
                    );
                    this.snackBar.open(
                        'Error al cargar categorías de reportes',
                        'Cerrar',
                        { duration: 5000, panelClass: ['error-snackbar'] }
                    );
                    return of({ success: false, data: [] });
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((res) => {
                if (res.success && res.data) {
                    this.dataSource.data = res.data.map(
                        (m: ReportCategoryMapping) => ({
                            id: m.id,
                            reportId: m.report_id,
                            reportName: m.report_name || '(sin nombre)',
                            category: m.category,
                        })
                    );
                    this.cdr.detectChanges();
                }
            });
    }

    deleteMapping(row: ReportCategoryRow): void {
        const d = this.dialog.open(DeleteReportCategoryDialogComponent, {
            width: '500px',
            maxWidth: '90vw',
            disableClose: true,
            data: { mapping: row },
        });
        d.afterClosed().subscribe((ok) => {
            if (ok) this.performDelete(row.id);
        });
    }

    private performDelete(id: string): void {
        this.isLoading = true;
        this.reportCategoryService
            .deleteMapping(id)
            .pipe(
                catchError((err) => {
                    const msg =
                        err.error?.message || 'Error al eliminar la categoría';
                    this.snackBar.open(msg, 'Cerrar', {
                        duration: 6000,
                        panelClass: ['error-snackbar'],
                    });
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((res) => {
                if (res?.success) {
                    this.loadMappings();
                    this.snackBar.open(
                        'Categoría eliminada exitosamente',
                        'Cerrar',
                        { duration: 3000, panelClass: ['success-snackbar'] }
                    );
                }
            });
    }

    onCreate(): void {
        this.router.navigate(['/modules/report-categories/create']);
    }

    onBack(): void {
        this.router.navigate(['/modules']);
    }

    isBaseRoute(): boolean {
        const u = this.router.url || '';
        return (
            u === '/modules/report-categories' ||
            u === '/modules/report-categories/' ||
            u.startsWith('/modules/report-categories?')
        );
    }

    getCategoryLabel(category: string): string {
        switch (category) {
            case 'operativo':
                return 'Operativo';
            case 'financiero':
                return 'Financiero';
            case 'gerencial':
                return 'Gerencial';
            default:
                return category;
        }
    }

    getCategoryClass(category: string): string {
        switch (category) {
            case 'operativo':
                return 'category-operativo';
            case 'financiero':
                return 'category-financiero';
            case 'gerencial':
                return 'category-gerencial';
            default:
                return '';
        }
    }
}
